/**
 * Column Redux Slice
 * Manages column state and async operations
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { columnService } from '../entities/column/api/columnService';
import { Column, CreateColumnDTO, UpdateColumnDTO, ColumnState } from '../entities/column/model/types';

const initialState: ColumnState = {
  columns: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchColumnsForBoard = createAsyncThunk(
  'columns/fetchColumnsForBoard',
  async (boardId: number, { rejectWithValue }) => {
    try {
      return await columnService.getColumnsForBoard(boardId);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch columns');
    }
  }
);

export const createColumn = createAsyncThunk(
  'columns/createColumn',
  async (columnData: CreateColumnDTO, { rejectWithValue }) => {
    try {
      return await columnService.createColumn(columnData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create column');
    }
  }
);

export const updateColumn = createAsyncThunk(
  'columns/updateColumn',
  async ({ columnId, updates }: { columnId: number; updates: UpdateColumnDTO }, { rejectWithValue }) => {
    try {
      return await columnService.updateColumn(columnId, updates);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update column');
    }
  }
);

export const deleteColumn = createAsyncThunk(
  'columns/deleteColumn',
  async (columnId: number, { rejectWithValue }) => {
    try {
      await columnService.deleteColumn(columnId);
      return columnId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete column');
    }
  }
);

export const reorderColumns = createAsyncThunk(
  'columns/reorderColumns',
  async ({ boardId, columnIds }: { boardId: number; columnIds: number[] }, { rejectWithValue }) => {
    try {
      return await columnService.reorderColumns(boardId, columnIds);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reorder columns');
    }
  }
);

// Slice
const columnSlice = createSlice({
  name: 'columns',
  initialState,
  reducers: {
    clearColumns: (state) => {
      state.columns = [];
      state.error = null;
    },
    clearColumnError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch columns
    builder.addCase(fetchColumnsForBoard.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchColumnsForBoard.fulfilled, (state, action: PayloadAction<Column[]>) => {
      state.loading = false;
      state.columns = action.payload;
    });
    builder.addCase(fetchColumnsForBoard.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Create column
    builder.addCase(createColumn.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createColumn.fulfilled, (state, action: PayloadAction<Column>) => {
      state.loading = false;
      state.columns.push(action.payload);
    });
    builder.addCase(createColumn.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Update column
    builder.addCase(updateColumn.fulfilled, (state, action: PayloadAction<Column>) => {
      const index = state.columns.findIndex((col) => col.id === action.payload.id);
      if (index !== -1) {
        state.columns[index] = action.payload;
      }
    });

    // Delete column
    builder.addCase(deleteColumn.fulfilled, (state, action: PayloadAction<number>) => {
      state.columns = state.columns.filter((col) => col.id !== action.payload);
    });

    // Reorder columns
    builder.addCase(reorderColumns.fulfilled, (state, action: PayloadAction<Column[]>) => {
      state.columns = action.payload;
    });
  },
});

export const { clearColumns, clearColumnError } = columnSlice.actions;
export default columnSlice.reducer;
